import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import useToast from '../../hooks/useToast';
import './CommonStyles.css';
import './Products.css';
import './ProductSearchStyles.css';
import './AddSellStyles.css';
import './ProductsActions.css';
import './AllSellStyles.css';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';

const STATUS_COLORS = {
  pending:    { bg: '#fee2e2', color: '#b91c1c', badgeClass: 'badge badge-danger' },
  in_transit: { bg: '#fef9c3', color: '#92400e', badgeClass: 'badge badge-warning' },
  completed:  { bg: '#dcfce7', color: '#15803d', badgeClass: 'badge badge-success' },
  final:      { bg: '#dcfce7', color: '#15803d', badgeClass: 'badge badge-success' },
};

const StockTransfers = ({ activeTab: propActiveTab }) => {
  const [activeTab, setActiveTab] = useState('list_transfers');

  // Synchronize state with prop changes from dashboard popover
  useEffect(() => {
    if (propActiveTab) {
      if (propActiveTab === 'stock-transfers') {
        setActiveTab('list_transfers');
      } else {
        setActiveTab(propActiveTab);
      }
    }
  }, [propActiveTab]);

  const { showSuccess, showError } = useToast();

  // ── Shared ────────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm]           = useState('');
  const [showFilters, setShowFilters]         = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // ── List ──────────────────────────────────────────────────────────────────
  const [transfers, setTransfers]     = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lastPage, setLastPage]       = useState(1);
  const [perPage, setPerPage]         = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading]     = useState(false);
  const [openActionId, setOpenActionId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });

  const [filters, setFilters] = useState({
    location_id: 'all',
    status: 'all',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    if (!openActionId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.action-btn-trigger') || e.target.closest('.action-dropdown-menu')) return;
      setOpenActionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  const toggleDropdown = (rowId, event) => {
    event.stopPropagation();
    if (openActionId === rowId) {
      setOpenActionId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const gap = 8;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuMinWidth = 220;
      const edgePadding = 8;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
      const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

      const left = Math.min(
        Math.max(rect.left, edgePadding),
        Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
      );

      setDropdownPosition({
        top: openUpwards ? null : (rect.bottom + gap),
        bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
        left,
        maxHeight
      });
      setOpenActionId(rowId);
    }
  };

  // ── View modal ────────────────────────────────────────────────────────────
  const [viewTransfer, setViewTransfer]   = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading]     = useState(false);

  // ── Delete confirm ────────────────────────────────────────────────────────
  const [deleteId, setDeleteId]           = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting]       = useState(false);

  // ── Status update ─────────────────────────────────────────────────────────
  const [statusTransfer, setStatusTransfer]   = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus]             = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // ── Add / Edit form ───────────────────────────────────────────────────────
  const [editId, setEditId]       = useState(null);
  const [locations, setLocations] = useState([]);
  const [isSaving, setIsSaving]   = useState(false);

  const getCurrentDateTime = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return (new Date(now - offset)).toISOString().slice(0, 16);
  };

  const emptyForm = {
    date: getCurrentDateTime(),
    referenceNo: '',
    status: 'pending',
    locationFrom: '',
    locationTo: '',
    shippingCharges: 0,
    note: '',
  };

  const [formData, setFormData] = useState(emptyForm);
  const [items, setItems] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  // ── Fetch list ────────────────────────────────────────────────────────────
  const fetchTransfers = useCallback(async (page = currentPage, search = searchTerm) => {
    setIsLoading(true);
    try {
      const params = { per_page: perPage, page, search };
      if (filters.location_id !== 'all') params.location_id = filters.location_id;
      if (filters.status !== 'all')      params.status      = filters.status;
      if (filters.start_date)            params.start_date  = filters.start_date;
      if (filters.end_date)              params.end_date    = filters.end_date;

      const response = await apiService.getStockTransfers(params);
      setTransfers(response.data || []);
      setTotalRecords(response.total || 0);
      setLastPage(response.last_page || 1);
    } catch (error) {
      /* console.error('Failed to fetch transfers', error); */
    } finally {
      setIsLoading(false);
    }
  }, [perPage, currentPage, searchTerm, filters]);

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchTransfers(1, searchTerm);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    const reset = { location_id: 'all', status: 'all', start_date: '', end_date: '' };
    setFilters(reset);
    setCurrentPage(1);
    setShowFilters(false);
    setTimeout(() => fetchTransfers(1, searchTerm), 0);
  };

  const fetchLocations = async () => {
    try {
      const response = await apiService.getBusinessLocations();
      setLocations(response.data || response || []);
    } catch (error) {
      /* console.error('Failed to fetch locations', error); */
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchTransfers(currentPage, searchTerm);
  }, [perPage, currentPage]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setCurrentPage(1);
      fetchTransfers(1, searchTerm);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ── Product search (form) ─────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      if (productSearchTerm.length > 1) {
        apiService.getProducts({ search: productSearchTerm }).then(res => {
          const results = res.data || res || [];
          setProductSearchResults(results);
          setShowProductDropdown(results.length > 0);
        }).catch(() => {});
      } else {
        setProductSearchResults([]);
        setShowProductDropdown(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [productSearchTerm]);

  // ── Form helpers ──────────────────────────────────────────────────────────
  const handleAddProduct = (product) => {
    const existingIdx = items.findIndex(i => i.id === product.id);
    if (existingIdx >= 0) {
      const newItems = [...items];
      newItems[existingIdx].quantity += 1;
      newItems[existingIdx].subtotal = newItems[existingIdx].quantity * newItems[existingIdx].unitPrice;
      setItems(newItems);
    } else {
      const unitPrice = parseFloat(
        product.sell_price_inc_tax ||
        product.default_sell_price ||
        product.purchase_price ||
        product.unit_price ||
        product.product_variations?.[0]?.variations?.[0]?.sell_price_inc_tax ||
        product.product_variations?.[0]?.variations?.[0]?.default_sell_price ||
        0
      );
      const variationId = product.product_variations?.[0]?.variations?.[0]?.id || null;
      setItems([...items, { id: product.id, name: product.name, sku: product.sku, variation_id: variationId, quantity: 1, unitPrice, subtotal: unitPrice }]);
    }
    setProductSearchTerm('');
    setShowProductDropdown(false);
  };

  const handleQtyChange = (index, val) => {
    const qty = parseFloat(val) || 0;
    const newItems = [...items];
    newItems[index].quantity = qty;
    newItems[index].subtotal = qty * newItems[index].unitPrice;
    setItems(newItems);
  };

  const handleUnitPriceChange = (index, val) => {
    const price = parseFloat(val) || 0;
    const newItems = [...items];
    newItems[index].unitPrice = price;
    newItems[index].subtotal = newItems[index].quantity * price;
    setItems(newItems);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const totalItemAmount = items.reduce((sum, i) => sum + i.subtotal, 0);
  const totalAmount = totalItemAmount + (parseFloat(formData.shippingCharges) || 0);

  // ── Save (create or update) ───────────────────────────────────────────────
  const handleSave = async () => {
    const missing = [];
    if (!formData.locationFrom) missing.push('Location (From)');
    if (!formData.locationTo)   missing.push('Location (To)');
    if (items.length === 0)     missing.push('at least one Product');
    if (missing.length > 0) { showError(`Please fill in: ${missing.join(', ')}`); return; }
    if (String(formData.locationFrom) === String(formData.locationTo)) {
      showError('Location (From) and Location (To) cannot be the same.');
      return;
    }

    const payload = {
      transaction_date: formData.date.replace('T', ' ') + ':00',
      ref_no: formData.referenceNo || undefined,
      status: formData.status,
      location_id: parseInt(formData.locationFrom),
      transfer_location_id: parseInt(formData.locationTo),
      shipping_charges: parseFloat(formData.shippingCharges) || 0,
      additional_notes: formData.note || '',
      final_total: totalAmount,
      products: items.map(item => ({
        product_id: item.id,
        variation_id: item.variation_id || null,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        enable_stock: 1,
      })),
    };

    setIsSaving(true);
    try {
      if (editId) {
        await apiService.updateStockTransfer(editId, payload);
        showSuccess('Stock transfer updated successfully!');
      } else {
        await apiService.createStockTransfer(payload);
        showSuccess('Stock transfer created successfully!');
      }
      handleCancelForm();
      fetchTransfers(1, '');
      setSearchTerm('');
      setCurrentPage(1);
    } catch (error) {
      showError(error.message || 'Failed to save stock transfer.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelForm = () => {
    setActiveTab('list_transfers');
    setEditId(null);
    setItems([]);
    setFormData(emptyForm);
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const handleEdit = async (transfer) => {
    setOpenActionId(null);
    try {
      const res = await apiService.getStockTransfer(transfer.id);
      const t = res.data;
      setEditId(t.id);
      setFormData({
        date: t.transaction_date ? t.transaction_date.slice(0, 16) : getCurrentDateTime(),
        referenceNo: t.ref_no || '',
        status: t.status === 'final' ? 'completed' : (t.status || 'pending'),
        locationFrom: res.location_from?.id || t.location_id || '',
        locationTo: res.location_to?.id || res.purchase_transfer?.location_id || '',
        shippingCharges: t.shipping_charges || 0,
        note: t.additional_notes || '',
      });
      setItems((t.sell_lines || []).map(line => ({
        id: line.product_id,
        name: line.product?.name || '',
        sku: line.product?.sku || '',
        variation_id: line.variation_id,
        transaction_sell_lines_id: line.id,
        quantity: parseFloat(line.quantity) || 1,
        unitPrice: parseFloat(line.unit_price) || 0,
        subtotal: (parseFloat(line.quantity) || 1) * (parseFloat(line.unit_price) || 0),
      })));
      setActiveTab('add_transfer');
    } catch (err) {
      showError('Failed to load transfer for editing.');
    }
  };

  // ── View ──────────────────────────────────────────────────────────────────
  const handleView = async (id) => {
    setOpenActionId(null);
    setViewLoading(true);
    setShowViewModal(true);
    try {
      const res = await apiService.getStockTransfer(id);
      setViewTransfer(res);
    } catch (err) {
      showError('Failed to load transfer details.');
      setShowViewModal(false);
    } finally {
      setViewLoading(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteClick = (id) => {
    setOpenActionId(null);
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await apiService.deleteStockTransfer(deleteId);
      showSuccess('Stock transfer deleted successfully.');
      setShowDeleteConfirm(false);
      setDeleteId(null);
      fetchTransfers(currentPage, searchTerm);
    } catch (err) {
      showError(err.message || 'Failed to delete stock transfer.');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Status update ─────────────────────────────────────────────────────────
  const handleStatusClick = (transfer) => {
    setOpenActionId(null);
    setStatusTransfer(transfer);
    setNewStatus(transfer.status);
    setShowStatusModal(true);
  };

  const handleStatusSave = async () => {
    setIsUpdatingStatus(true);
    try {
      await apiService.updateStockTransferStatus(statusTransfer.id, newStatus);
      showSuccess('Status updated successfully.');
      setShowStatusModal(false);
      setStatusTransfer(null);
      fetchTransfers(currentPage, searchTerm);
    } catch (err) {
      showError(err.message || 'Failed to update status.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = (format) => {
    if (!transfers || transfers.length === 0) { showError('No data to export'); setShowExportDropdown(false); return; }
    const headers = ['Date', 'Reference No', 'Location (From)', 'Location (To)', 'Status', 'Shipping Charges', 'Total Amount', 'Additional Notes'];
    const rows = transfers.map(t => [
      t.transaction_date || '', t.ref_no || '', t.location_from || '', t.location_to || '',
      t.status || '', t.shipping_charges || '0.00', t.final_total || '0.00', t.additional_notes || '',
    ]);
    if (format === 'CSV')   exportToCSV(headers, rows, 'stock_transfers');
    else if (format === 'Excel') exportToExcel(headers, rows, 'stock_transfers');
    else if (format === 'PDF')   exportToPDF('Stock Transfers', headers, rows, 'stock_transfers');
    else if (format === 'Print') printPage();
    setShowExportDropdown(false);
  };

  // ── Pagination helpers ────────────────────────────────────────────────────
  const fromEntry = totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const toEntry   = Math.min(currentPage * perPage, totalRecords);

  // ── Shared input style ────────────────────────────────────────────────────
  const inputStyle = { width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none', fontSize: '0.875rem' };
  const labelStyle = { fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER — ADD / EDIT FORM
  // ════════════════════════════════════════════════════════════════════════════
  if (activeTab === 'add_transfer') {
    return (
      <div className="products-container">
        <div className="main-card">
          <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0' }}>
            <button type="button" onClick={handleCancelForm} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '1.2rem' }}>←</button>
            <h2 style={{ fontSize: '1.25rem', margin: 0 }}>{editId ? 'Edit Stock Transfer' : 'Add Stock Transfer'}</h2>
          </div>

          <div style={{ padding: '1.5rem' }}>
            {/* Top fields */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Date:*</label>
                <input type="datetime-local" style={inputStyle} value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Reference No:</label>
                <input type="text" style={inputStyle} placeholder="Leave empty to auto-generate" value={formData.referenceNo} onChange={e => setFormData({ ...formData, referenceNo: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Status:*</label>
                <select style={inputStyle} value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                  <option value="pending">Pending</option>
                  <option value="in_transit">In Transit</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Location (From):*</label>
                <select style={inputStyle} value={formData.locationFrom} onChange={e => setFormData({ ...formData, locationFrom: e.target.value })}>
                  <option value="">Select Location</option>
                  {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name} ({loc.location_id})</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Location (To):*</label>
                <select style={inputStyle} value={formData.locationTo} onChange={e => setFormData({ ...formData, locationTo: e.target.value })}>
                  <option value="">Select Location</option>
                  {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name} ({loc.location_id})</option>)}
                </select>
                {formData.locationFrom && formData.locationTo && String(formData.locationFrom) === String(formData.locationTo) && (
                  <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>Please select different location</p>
                )}
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '1.5rem 0' }} />

            {/* Product search */}
            <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Search products for stock adjustment</label>
              <div style={{ position: 'relative' }}>
                <input type="text" placeholder="Search products for stock adjustment" value={productSearchTerm}
                  onChange={e => setProductSearchTerm(e.target.value)}
                  onFocus={() => productSearchTerm.length > 1 && setShowProductDropdown(true)}
                  autoComplete="off"
                  style={{ ...inputStyle, paddingLeft: '2.5rem', border: '1px solid #3b82f6', boxShadow: '0 0 0 3px rgba(59,130,246,0.1)' }} />
                <i className="fa fa-search" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#3b82f6' }}></i>
              </div>
              {showProductDropdown && productSearchResults.length > 0 && (
                <div style={{ position: 'absolute', width: '100%', zIndex: 100, background: 'white', border: '1px solid #e2e8f0', borderRadius: '0.5rem', marginTop: '4px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', maxHeight: '300px', overflowY: 'auto' }}>
                  {productSearchResults.map(p => (
                    <div key={p.id} onMouseDown={e => { e.preventDefault(); handleAddProduct(p); }}
                      style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                      <span style={{ fontWeight: '600' }}>{p.name}</span> <span style={{ color: '#64748b', fontSize: '0.85rem' }}>({p.sku})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Products table */}
            <div className="product-table-container" style={{ marginBottom: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '0.75rem', background: 'var(--etpos-card, #fff)', overflowX: 'auto' }}>
              <table className="products-table" style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.8125rem', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ background: 'var(--etpos-bg, #f8fafc)', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '0.5rem 0.6rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: '700', width: '55%' }}>PRODUCT</th>
                    <th style={{ padding: '0.5rem 0.6rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: '700', width: '10%' }}>QUANTITY</th>
                    <th style={{ padding: '0.5rem 0.6rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: '700', width: '12%' }}>UNIT PRICE</th>
                    <th style={{ padding: '0.5rem 0.6rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: '700', width: '13%' }}>SUBTOTAL</th>
                    <th style={{ padding: '0.5rem 0.6rem', color: 'var(--etpos-text-muted, #475569)', fontWeight: '700', textAlign: 'center', width: '10%' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '3rem', color: 'var(--etpos-text-muted, #94a3b8)' }}>No products added yet.</td>
                    </tr>
                  ) : items.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.5rem 0.6rem' }}>
                        <div style={{ fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{item.name}</div>
                        {item.sku && <div style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted, #64748b)' }}>SKU: {item.sku}</div>}
                      </td>
                      <td style={{ padding: '0.5rem 0.6rem' }}>
                        <input type="number" min="0.01" step="0.01" value={item.quantity}
                          onChange={e => handleQtyChange(idx, e.target.value)}
                          style={{ width: '100%', maxWidth: '70px', padding: '0.4rem', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', fontSize: '0.8125rem' }} />
                      </td>
                      <td style={{ padding: '0.5rem 0.6rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>
                        <input type="number" min="0" step="0.01" value={item.unitPrice}
                          onChange={e => handleUnitPriceChange(idx, e.target.value)}
                          style={{ width: '100%', maxWidth: '90px', padding: '0.4rem', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', fontSize: '0.8125rem' }} />
                      </td>
                      <td style={{ padding: '0.5rem 0.6rem', fontWeight: '700', color: 'var(--etpos-text, #1e293b)' }}>${item.subtotal.toFixed(2)}</td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'center' }}>
                        <button type="button" onClick={() => handleRemoveItem(idx)}
                          style={{ color: '#ef4444', background: 'rgba(239, 68, 68, 0.1)', border: 'none', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {items.length > 0 && (
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #e2e8f0', background: 'var(--etpos-bg, #f8fafc)' }}>
                      <td colSpan="3" style={{ padding: '0.6rem', textAlign: 'right', fontWeight: '700', color: 'var(--etpos-text-muted, #64748b)' }}>Total:</td>
                      <td style={{ padding: '0.6rem', fontWeight: '900', color: '#2563eb' }}>${totalAmount.toFixed(2)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* Bottom fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={labelStyle}>Shipping Charges:</label>
                <input type="number" min="0" step="0.01" style={inputStyle} value={formData.shippingCharges}
                  onChange={e => setFormData({ ...formData, shippingCharges: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Additional Notes:</label>
                <textarea rows="2" style={{ ...inputStyle, resize: 'vertical' }} value={formData.note}
                  onChange={e => setFormData({ ...formData, note: e.target.value })} placeholder="Additional notes..." />
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
              <button type="button" onClick={handleCancelForm} disabled={isSaving}
                style={{ padding: '0.7rem 2rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: '700', color: '#475569', cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="button" onClick={handleSave} disabled={isSaving}
                style={{ padding: '0.7rem 2.5rem', background: isSaving ? '#93c5fd' : '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: isSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {isSaving ? (<><span style={{ width: 14, height: 14, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />Saving...</>) : (editId ? 'Update' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER — LIST VIEW
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="products-container">
      <div className="main-card">

        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input type="text" placeholder="Search..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)} className="tabs-search-input" />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)} title="Open Filters">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={() => { setEditId(null); setItems([]); setFormData(emptyForm); setActiveTab('add_transfer'); }}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        <div className="products-table-container">
            {isLoading ? (
              <div className="loading-sales">
                <h3>⏳ Loading Stock Transfers...</h3>
                <p>Fetching stock transfer data from server...</p>
              </div>
            ) : transfers.length === 0 ? (
              <div className="no-sales">
                <h3>📊 No Stock Transfers Found</h3>
                <p>No stock transfers have been recorded yet.</p>
              </div>
            ) : (
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Date</th>
                    <th>Reference No</th>
                    <th>Location (From)</th>
                    <th>Location (To)</th>
                    <th>Status</th>
                    <th>Shipping Charges</th>
                    <th>Total Amount</th>
                    <th>Additional Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.map(transfer => {
                    const sc = STATUS_COLORS[transfer.status] || STATUS_COLORS.pending;
                    return (
                      <tr key={transfer.id}>
                        <td className="actions-cell">
                          <div className="action-dropdown-container">
                            <button className="action-btn-trigger" onClick={(e) => toggleDropdown(transfer.id, e)}>
                              Actions <span className="arrow-down">▼</span>
                            </button>
                            {openActionId === transfer.id && ReactDOM.createPortal(
                              <div
                                className="action-dropdown-menu"
                                style={{
                                  position: 'fixed',
                                  top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                  bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                  left: `${dropdownPosition.left}px`,
                                  maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                  zIndex: 9999999
                                }}
                              >
                                <button className="dropdown-item" onClick={() => handleView(transfer.id)}>👁️ View</button>
                                {transfer.status !== 'completed' && transfer.status !== 'final' && (
                                  <button className="dropdown-item" onClick={() => handleEdit(transfer)}>✏️ Edit</button>
                                )}
                                {transfer.status !== 'completed' && transfer.status !== 'final' && (
                                  <button className="dropdown-item" onClick={() => handleStatusClick(transfer)}>🔄 Update Status</button>
                                )}
                                <button className="dropdown-item delete-item" onClick={() => handleDeleteClick(transfer.id)}>🗑️ Delete</button>
                              </div>,
                              document.body
                            )}
                          </div>
                        </td>
                        <td>{transfer.transaction_date ? transfer.transaction_date.slice(0, 16).replace('T', ' ') : '-'}</td>
                        <td>{transfer.ref_no || '-'}</td>
                        <td>{transfer.location_from || '-'}</td>
                        <td>{transfer.location_to || '-'}</td>
                        <td>
                          <span className={sc.badgeClass || 'badge badge-info'}>
                            {transfer.status ? transfer.status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-'}
                          </span>
                        </td>
                        <td>${parseFloat(transfer.shipping_charges || 0).toFixed(2)}</td>
                        <td>${parseFloat(transfer.final_total || 0).toFixed(2)}</td>
                        <td>{transfer.additional_notes || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.875rem', color: '#475569' }}>
              Showing {fromEntry} to {toEntry} of {totalRecords} entries
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="pagination-btn" disabled={currentPage <= 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Previous</button>
              <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem' }}>
                Page {currentPage} of {lastPage}
              </span>
              <button className="pagination-btn" disabled={currentPage >= lastPage}
                onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}>Next</button>
            </div>
          </div>
      </div>

      {/* ── View Modal ────────────────────────────────────────────────────── */}
      {showViewModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowViewModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }}>
          <div className="modal-container" onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', maxWidth: '700px', width: '90%', maxHeight: '90vh', overflowY: 'auto', padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>Stock Transfer Details</h3>
              <button onClick={() => setShowViewModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            <div>
              {viewLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading...</div>
              ) : viewTransfer ? (() => {
                const t = viewTransfer.data || viewTransfer;
                const sc = STATUS_COLORS[t.status] || STATUS_COLORS.pending;
                return (
                  <>
                    <table className="products-table" style={{ marginBottom: '1.5rem' }}>
                      <tbody>
                        <tr><td style={{ fontWeight: '600', width: '35%' }}>Reference No</td><td>{t.ref_no || '-'}</td></tr>
                        <tr><td style={{ fontWeight: '600' }}>Date</td><td>{t.transaction_date ? t.transaction_date.slice(0, 16).replace('T', ' ') : '-'}</td></tr>
                        <tr><td style={{ fontWeight: '600' }}>Location (From)</td><td>{viewTransfer.location_from?.name || t.location_from || '-'}</td></tr>
                        <tr><td style={{ fontWeight: '600' }}>Location (To)</td><td>{viewTransfer.location_to?.name || t.location_to || '-'}</td></tr>
                        <tr><td style={{ fontWeight: '600' }}>Status</td><td><span className={sc.badgeClass || 'badge badge-info'}>{(t.status || '').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</span></td></tr>
                        <tr><td style={{ fontWeight: '600' }}>Shipping Charges</td><td>${parseFloat(t.shipping_charges || 0).toFixed(2)}</td></tr>
                        <tr><td style={{ fontWeight: '600' }}>Total Amount</td><td>${parseFloat(t.final_total || 0).toFixed(2)}</td></tr>
                        <tr><td style={{ fontWeight: '600' }}>Additional Notes</td><td>{t.additional_notes || '-'}</td></tr>
                      </tbody>
                    </table>
                    {t.sell_lines && t.sell_lines.length > 0 && (
                      <>
                        <h4 style={{ fontSize: '1rem', marginBottom: '0.75rem', color: '#334155' }}>Products</h4>
                        <div style={{ overflowX: 'auto' }}>
                          <table className="products-table">
                            <thead>
                              <tr>
                                <th>Product</th>
                                <th style={{ textAlign: 'right' }}>Qty</th>
                                <th style={{ textAlign: 'right' }}>Unit Price</th>
                                <th style={{ textAlign: 'right' }}>Subtotal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {t.sell_lines.map((line, idx) => (
                                <tr key={idx}>
                                  <td style={{ fontWeight: '500' }}>{line.product?.name || line.product_id}</td>
                                  <td style={{ textAlign: 'right' }}>{parseFloat(line.quantity).toFixed(2)}</td>
                                  <td style={{ textAlign: 'right' }}>${parseFloat(line.unit_price || 0).toFixed(2)}</td>
                                  <td style={{ textAlign: 'right' }}>${(parseFloat(line.quantity) * parseFloat(line.unit_price || 0)).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </>
                );
              })() : null}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <button className="pagination-btn" onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Delete Confirm ────────────────────────────────────────────────── */}
      {showDeleteConfirm && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteConfirm(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }}>
          <div className="modal-container" onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', maxWidth: '420px', width: '90%', padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>Delete Stock Transfer</h3>
              <button onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            <div style={{ padding: '0.5rem 0 1.5rem', color: '#475569' }}>
              Are you sure you want to delete this stock transfer? This action cannot be undone.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <button className="pagination-btn" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>Cancel</button>
              <button onClick={handleDeleteConfirm} disabled={isDeleting}
                style={{ padding: '0.5rem 1.5rem', background: isDeleting ? '#fca5a5' : '#dc2626', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: isDeleting ? 'not-allowed' : 'pointer' }}>
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Status Update Modal ───────────────────────────────────────────── */}
      {showStatusModal && statusTransfer && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => !isUpdatingStatus && setShowStatusModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }}>
          <div className="modal-container" onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', maxWidth: '380px', width: '90%', padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>Update Status</h3>
              <button onClick={() => setShowStatusModal(false)} disabled={isUpdatingStatus} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            <div style={{ padding: '0.5rem 0 1.5rem' }}>
              <label style={labelStyle}>New Status for <strong>{statusTransfer.ref_no}</strong>:</label>
              <select value={newStatus} onChange={e => setNewStatus(e.target.value)} style={inputStyle}>
                <option value="pending">Pending</option>
                <option value="in_transit">In Transit</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <button className="pagination-btn" onClick={() => setShowStatusModal(false)} disabled={isUpdatingStatus}>Cancel</button>
              <button onClick={handleStatusSave} disabled={isUpdatingStatus}
                style={{ padding: '0.5rem 1.5rem', background: isUpdatingStatus ? '#93c5fd' : '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: isUpdatingStatus ? 'not-allowed' : 'pointer' }}>
                {isUpdatingStatus ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Filters Modal ─────────────────────────────────────────────────── */}
      {showFilters && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowFilters(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999999 }}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', maxWidth: '600px', width: '90%', padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>Filters</h3>
              <button onClick={() => setShowFilters(false)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '0.5rem 0' }}>
              <div className="filter-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="filter-group">
                  <label style={labelStyle}>Location:</label>
                  <select style={inputStyle} value={filters.location_id} onChange={(e) => setFilters({...filters, location_id: e.target.value})}>
                    <option value="all">All Locations</option>
                    {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label style={labelStyle}>Status:</label>
                  <select style={inputStyle} value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})}>
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="in_transit">In Transit</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label style={labelStyle}>Start Date:</label>
                  <input type="date" style={inputStyle} value={filters.start_date} onChange={(e) => setFilters({...filters, start_date: e.target.value})} />
                </div>
                <div className="filter-group">
                  <label style={labelStyle}>End Date:</label>
                  <input type="date" style={inputStyle} value={filters.end_date} onChange={(e) => setFilters({...filters, end_date: e.target.value})} />
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0' }}>
              <button onClick={handleResetFilters} style={{ padding: '0.6rem 1.25rem', border: '1px solid #e2e8f0', background: '#fff', borderRadius: '8px', cursor: 'pointer', color: '#1e293b' }}>Reset</button>
              <button onClick={handleApplyFilters} style={{ padding: '0.6rem 1.25rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Apply Filters</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default StockTransfers;
